import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Send, Save, Clock } from "lucide-react";

export default function EmailReportsPanel() {
  const [cfg, setCfg] = useState(null);
  const [pwd, setPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/email-config");
      setCfg(r.data);
    } catch { toast.error("Failed to load email config"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="text-sm text-neutral-500">Loading…</div>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const addRecipient = () => {
    const e = newEmail.trim();
    if (!e || !/.+@.+\..+/.test(e)) return toast.error("Enter a valid email");
    if ((cfg.recipients || []).includes(e)) return toast.error("Already added");
    set("recipients", [...(cfg.recipients || []), e]);
    setNewEmail("");
  };
  const removeRecipient = (e) => set("recipients", (cfg.recipients || []).filter((x) => x !== e));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...cfg, public_base_url: window.location.origin };
      if (pwd) body.smtp_password = pwd;
      delete body.has_password;
      const r = await api.put("/admin/email-config", body);
      setCfg(r.data);
      setPwd("");
      toast.success("Email settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSending(true);
    toast.info("Rendering images & sending… this can take ~20s");
    try {
      const r = await api.post("/admin/email/send-now", { public_base_url: window.location.origin });
      toast.success(`Sent ${r.data.attachments.length} images to ${r.data.recipients.length} recipient(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const input = "brutal-input w-full";
  return (
    <div className="space-y-6" data-testid="email-reports-panel">
      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-1 flex items-center gap-2"><Mail size={18} /> Automatic Report Email</h3>
        <p className="text-sm text-neutral-600 mb-4">
          Every day at the set time (IST) the system emails <strong>today's Questions</strong>, the <strong>previous day's Answers</strong>, and the <strong>previous day's Winners</strong> images to your recipients. Dates auto-fill in the message.
        </p>

        {/* Auto-send toggle + time */}
        <div className="flex flex-wrap items-end gap-4 mb-5 border-2 border-black p-3 bg-neutral-50">
          <label className="flex items-center gap-2 font-black uppercase text-sm">
            <input data-testid="email-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-5 h-5" />
            Auto-send enabled
          </label>
          <div>
            <label className="text-[11px] font-black uppercase block flex items-center gap-1"><Clock size={12} /> Send time (IST)</label>
            <div className="flex items-center gap-1">
              <input data-testid="email-send-hour" type="number" min="0" max="23" value={cfg.send_hour} onChange={(e) => set("send_hour", Math.max(0, Math.min(23, +e.target.value)))} className="brutal-input w-20" />
              <span className="font-black">:</span>
              <input data-testid="email-send-minute" type="number" min="0" max="59" value={cfg.send_minute} onChange={(e) => set("send_minute", Math.max(0, Math.min(59, +e.target.value)))} className="brutal-input w-20" />
            </div>
          </div>
          {cfg.last_result && <div className="text-xs text-neutral-500">Last: {cfg.last_result}</div>}
        </div>

        {/* Sender (SMTP) config */}
        <div className="mb-2 text-xs font-black uppercase tracking-widest text-neutral-500">Sender (SMTP) — the mailbox emails are sent from</div>
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div><label className="text-[11px] font-black uppercase block">SMTP Host</label><input data-testid="email-smtp-host" className={input} placeholder="smtp.office365.com / smtp.gmail.com" value={cfg.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">SMTP Port</label><input data-testid="email-smtp-port" type="number" className={input} placeholder="587" value={cfg.smtp_port || 587} onChange={(e) => set("smtp_port", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">From Email</label><input data-testid="email-from" className={input} placeholder="funzone@tekwissen.com" value={cfg.from_email || ""} onChange={(e) => set("from_email", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">From Name</label><input data-testid="email-from-name" className={input} placeholder="TekWissen Fun Zone" value={cfg.from_name || ""} onChange={(e) => set("from_name", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">SMTP Username</label><input data-testid="email-smtp-user" className={input} placeholder="(defaults to From Email)" value={cfg.smtp_user || ""} onChange={(e) => set("smtp_user", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">SMTP Password {cfg.has_password && <span className="text-green-600 normal-case">· set</span>}</label><input data-testid="email-smtp-pass" type="password" className={input} placeholder={cfg.has_password ? "•••••• (leave blank to keep)" : "app password"} value={pwd} onChange={(e) => setPwd(e.target.value)} /></div>
        </div>

        {/* Recipients */}
        <div className="mb-2 text-xs font-black uppercase tracking-widest text-neutral-500">Recipients — who receives the email</div>
        <div className="flex gap-2 mb-3">
          <input data-testid="email-new-recipient" className="brutal-input flex-1" placeholder="development@tekwissen.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRecipient()} />
          <button data-testid="email-add-recipient" onClick={addRecipient} className="brutal-btn-secondary flex items-center gap-1 px-4"><Plus size={16} /> Add</button>
        </div>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="email-recipient-list">
          {(cfg.recipients || []).length === 0 && <span className="text-sm text-neutral-400">No recipients yet — add at least one.</span>}
          {(cfg.recipients || []).map((e) => (
            <span key={e} data-testid={`email-recipient-${e}`} className="inline-flex items-center gap-2 border-2 border-black px-2 py-1 text-sm bg-white">
              {e}
              <button data-testid={`email-remove-${e}`} onClick={() => removeRecipient(e)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
            </span>
          ))}
        </div>

        {/* Message templates */}
        <div className="mb-2 text-xs font-black uppercase tracking-widest text-neutral-500">Message — use <code>{"{prev_date}"}</code> and <code>{"{today_date}"}</code> tokens</div>
        <div className="grid gap-3 mb-5">
          <div><label className="text-[11px] font-black uppercase block">Subject</label><input data-testid="email-subject" className={input} value={cfg.subject_template || ""} onChange={(e) => set("subject_template", e.target.value)} /></div>
          <div><label className="text-[11px] font-black uppercase block">Body</label><textarea data-testid="email-body" rows={7} className={input} value={cfg.body_template || ""} onChange={(e) => set("body_template", e.target.value)} /></div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button data-testid="email-save" onClick={save} disabled={busy} className="brutal-btn flex items-center gap-2 disabled:opacity-50"><Save size={16} /> {busy ? "Saving…" : "Save Settings"}</button>
          <button data-testid="email-send-now" onClick={sendNow} disabled={sending} className="brutal-btn-secondary flex items-center gap-2 disabled:opacity-50"><Send size={16} /> {sending ? "Sending…" : "Send Now (test)"}</button>
        </div>
      </div>
    </div>
  );
}
